import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faCircleUser } from '@fortawesome/pro-regular-svg-icons/faCircleUser';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { TagType } from '@fe-learner-common';
import { TSelfregSessionStates } from '@routes/selfreg/utils/getSessionStates';
import { TSessionStates } from '@routes/session-details/utils/getSessionStates';

export type Tag = {
  icon: IconDefinition;
  type: TagType;
  text: string;
};

type Props = {
  waitlistSeatNumber?: number | null;
};

type CompoundTagStates = TSessionStates & TSelfregSessionStates;

export const useTagMap = ({ waitlistSeatNumber }: Props) => {
  const intl = useIntl();

  const tagMap: Record<CompoundTagStates, Tag> = useMemo(
    () => ({
      waitlistFull: {
        icon: faExclamationCircle,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.status.waitlist.full',
        }),
      },
      sessionFull: {
        icon: faExclamationCircle,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.status.coursefull',
        }),
      },
      managerApprovalRequired: {
        icon: faCircleUser,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.enrollment.managerapproval',
        }),
      },
      disenrollmentUnavailable: {
        icon: faExclamationCircle,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.disenrollment.unavailable',
        }),
      },
      placedInWaitlist: {
        icon: faClock,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.status.waitlist.unnumbered',
        }),
      },
      numberedPlaceInWaitlist: {
        icon: faClock,
        type: 'attention',
        text: intl.formatMessage(
          {
            id: 'learner.common.course.status.waitlist.numbered',
          },
          { count: (waitlistSeatNumber || 0) + 1 },
        ),
      },
      enrollmentPending: {
        icon: faCircleUser,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.status.enrollment.confirmed',
        }),
      },
      enrollmentPendingManagerApproval: {
        icon: faCircleUser,
        type: 'attention',
        text: intl.formatMessage({
          id: 'learner.common.course.status.enrollment.pending.manager',
        }),
      },
    }),
    [intl, waitlistSeatNumber],
  );

  return tagMap;
};
